import React, { createContext, useContext, useState, useCallback } from 'react';
import { Alert, Snackbar } from '@mui/material';

/**
 * Context for displaying notification messages throughout the app
 */
export const NotificationContext = createContext();

/**
 * Provider component for notification system
 * Displays snackbar alerts for various app messages
 */
export function NotificationProvider({ children }) {
  /* eslint-disable no-param-reassign */
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'info', // 'success', 'info', 'warning', 'error'
  });

  /**
   * Show a notification message
   * @param {string} message - The message to display
   * @param {string} type - Alert type ('success', 'info', 'warning', 'error')
   * @param {number} duration - How long to show the message in ms
   */
        const showNotification = useCallback((message, type = 'info', duration = 4000) => {
    setNotification({
      open: true,
      message,
      type,
      duration,
    });
  }, []);

  /**
   * Close the notification
   */
  const hideNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false,
    }));
  };

    // expose to non-React utilities (e.g., fetchWithAuth)
  React.useEffect(() => {
    window.__showNotification = showNotification;
    return () => {
      delete window.__showNotification;
    };
  }, [showNotification]);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.duration || 4000}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={hideNotification} 
          severity={notification.type} 
          variant="filled" 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}

/**
 * Custom hook to use the notification context
 * @returns {Object} - Notification context methods
 */
export function useNotification() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}
